/**
 * S04 · Camera HUD — real `getUserMedia` viewfinder.
 *
 * Shutter grabs the current frame as a JPEG, writes it to OPFS via
 * `createPhotoCapture`, and navigates to the capture detail view.
 * If camera permission is denied or unavailable, the shutter instead
 * produces a synthesized fallback image so the flow still exercises
 * the OPFS + sync queue path end-to-end.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Chip, GestureBar, I, Ico, StatusBar, T } from '@/components/primitives';
import { useCamera } from '@/lib/device/useCamera';
import { useGeolocation } from '@/lib/device/useGeolocation';
import {
  useCaptureSessionStore,
  useCreatePhotoMutation,
  useSyncStatus,
} from '@kapture/client-core';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';
import { SubjectChip } from '@/components/SubjectChip';

export const Route = createFileRoute('/sessions/$sessionId/capture/photo')({
  component: CameraHudPage,
  staticData: { mobile: true, fullscreen: true },
});

function CameraHudPage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/capture/photo">
      <CameraHudContent />
    </LayoutOrLeaf>
  );
}

function CameraHudContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const camera = useCamera();
  const geo = useGeolocation();
  const createPhoto = useCreatePhotoMutation();
  // Set by a `photos` form field that sent us here; null when the dial did.
  const linkedFieldId = useCaptureSessionStore((st) => st.linkedFieldId);
  const setLinkedField = useCaptureSessionStore((st) => st.setLinkedField);
  const recentHashes = useCaptureSessionStore((st) => st.recentHashes);
  const rememberHash = useCaptureSessionStore((st) => st.rememberHash);
  const { online } = useSyncStatus();
  const [saving, setSaving] = useState(false);
  const [shotError, setShotError] = useState<string | null>(null);

  useEffect(() => {
    void camera.start();
    void geo.request({ timeout: 4000 }).catch(() => null);
    // stop() on unmount is handled inside useCamera.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() {
    camera.stop();
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  async function shoot() {
    if (saving) return;
    setSaving(true);
    setShotError(null);
    try {
      // The hashes of the last few frames from this run, so a retake the
      // inspector forgot they took is caught while the subject is still in
      // front of them. Three days later a reviewer can only send it back.
      const frame = await camera.capture(recentHashes);
      if (frame.quality?.hash) rememberHash(frame.quality.hash);
      const capture = await createPhoto.mutateAsync({
        sessionId,
        media: frame.blob,
        byteSize: frame.blob.size,
        width: frame.width,
        height: frame.height,
        gps: geo.point ?? null,
        linkedFieldId,
      });
      // Consumed. Left set, the next photo started from the dial would attach
      // itself to a field the inspector never chose.
      setLinkedField(null);
      camera.stop();
      // Route through Annotate (S05) first — user can save annotations or
      // skip straight through to CaptureDetail.
      navigate({
        to: '/sessions/$sessionId/capture/photo/review/$captureId',
        params: { sessionId, captureId: capture.id },
        // Carried rather than recomputed: the review screen has the stored
        // JPEG, not the frame, and re-measuring a re-encoded image would
        // answer a slightly different question than the one asked at the
        // shutter.
        search: frame.quality?.advice ? { advice: frame.quality.advice } : undefined,
      });
    } catch (err) {
      // Previously `try/finally` with no `catch`. Any throw in here — the
      // canvas fallback failing to decode, a full OPFS quota, a rejected
      // mutation — left the shutter looking alive and doing nothing, with no
      // message and nothing in the timeline. On site that reads as a broken
      // camera, and the inspector's remedy is to press it again.
      setShotError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  const degraded =
    camera.status === 'denied' || camera.status === 'unsupported' || camera.status === 'error';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: T.hudBg,
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.font,
        overflow: 'hidden',
      }}
    >
      <video
        ref={camera.videoRef}
        autoPlay
        playsInline
        muted
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          background: '#000',
        }}
      />
      {degraded && (
        <DegradedBackdrop message={camera.error ?? 'Camera unavailable — fallback mode'} />
      )}

      {/* Aperture corner brackets — the K mark's viewfinder, live on the
          frame being captured. Orange only while the shutter is working. */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none' }}>
        {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => {
          const stroke = saving ? T.brand : 'rgba(245,244,241,0.55)';
          const base = {
            position: 'absolute' as const,
            width: 22,
            height: 22,
            borderColor: stroke,
            borderStyle: 'solid',
            transition: 'border-color 120ms ease',
          };
          const map = {
            tl: { top: 96, left: 18, borderWidth: '2.5px 0 0 2.5px', borderTopLeftRadius: 4 },
            tr: { top: 96, right: 18, borderWidth: '2.5px 2.5px 0 0', borderTopRightRadius: 4 },
            bl: {
              bottom: 132,
              left: 18,
              borderWidth: '0 0 2.5px 2.5px',
              borderBottomLeftRadius: 4,
            },
            br: {
              bottom: 132,
              right: 18,
              borderWidth: '0 2.5px 2.5px 0',
              borderBottomRightRadius: 4,
            },
          };
          return <div key={corner} style={{ ...base, ...map[corner] }} />;
        })}
      </div>

      <div style={{ position: 'relative', zIndex: 10 }}>
        <StatusBar translucent />
      </div>
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button type="button" onClick={close} aria-label="Close camera" style={hudIconBtn}>
          <Ico d={I.close} size={16} />
        </button>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          <SubjectChip />
          <Chip tone="hud" size="sm">
            <span style={{ fontFamily: T.mono, letterSpacing: '0.08em' }}>
              REC ·{' '}
              {sessionId
                .replace(/[^a-z0-9]/gi, '')
                .slice(0, 8)
                .toUpperCase()}
            </span>
          </Chip>
          {geo.point && (
            <Chip tone="hud" size="sm" icon={<Ico d={I.pin} size={11} />}>
              GPS · ±{Math.round(geo.point.accuracyMeters ?? 0)}m
            </Chip>
          )}
          {!online && (
            <Chip tone="hud" size="sm" icon={<Ico d={I.offline} size={11} />}>
              Offline — saved to device
            </Chip>
          )}
        </div>
        <div style={{ width: 36 }} aria-hidden />
      </div>

      <div style={{ flex: 1 }} />

      {/* Capture state is always written out, never colour alone. */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          fontFamily: T.mono,
          fontSize: 10,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: shotError ? T.danger : saving ? T.brand : 'rgba(245,244,241,0.6)',
        }}
        role="status"
      >
        {shotError ? `Could not save — ${shotError}` : saving ? 'Saving frame…' : 'Photo · ready'}
      </div>

      {/* Shutter tray */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '12px 20px 18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background:
            'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.75) 100%)',
        }}
      >
        <div style={{ width: 48, height: 48 }} aria-hidden />
        <button
          type="button"
          onClick={() => void shoot()}
          disabled={saving}
          aria-label="Capture photo"
          style={{
            width: 74,
            height: 74,
            borderRadius: 37,
            border: `4px solid ${saving ? T.brand : '#fff'}`,
            padding: 4,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            transition: 'border-color 120ms ease',
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: saving ? T.brand : '#fff',
              transition: 'background 120ms ease',
            }}
          />
        </button>
        <button
          type="button"
          aria-label="Switch camera"
          onClick={() => void camera.start()}
          style={hudIconBtn}
        >
          <Ico d={I.refresh} size={20} />
        </button>
      </div>
      <GestureBar dark />
    </div>
  );
}

const hudIconBtn = {
  width: 36,
  height: 36,
  borderRadius: 18,
  background: T.hudChip,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  border: 'none',
  backdropFilter: 'blur(8px)',
  cursor: 'pointer',
} as const;

function DegradedBackdrop({ message }: { message: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'radial-gradient(circle at center, rgba(40,44,52,1) 0%, rgba(10,12,16,1) 80%)',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: 24,
          maxWidth: 280,
          color: 'rgba(255,255,255,0.78)',
          fontFamily: T.font,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'rgba(255,255,255,0.08)',
            margin: '0 auto 14px',
            display: 'grid',
            placeItems: 'center',
            color: T.brand,
          }}
        >
          <Ico d={I.camera} size={22} stroke={T.brand} />
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 6 }}>
          Camera unavailable
        </div>
        <div>{message}</div>
        <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
          Shutter still works — a synthesized photo will be saved so you can exercise the flow.
        </div>
      </div>
    </div>
  );
}
